// Labels and action copy for provider-operation recovery cases (SM-661,
// SM-621 phase 14). Every value is a closed contract enum (contracts
// broker/v1/funding_recovery.go); an unknown value renders as the wire
// string, so a value added upstream is still shown, never hidden.
import type { RecoveryAction, RecoveryCaseItem } from "@/lib/broker-types";

import type { Tone } from "./governance";
import { launchOutcomeClassLabel } from "./launchAudit";

const KINDS: Record<string, { label: string }> = {
  CONTRACT_VIOLATION: { label: "Contract violation" },
  UNRESOLVED_ESCALATED: { label: "Unresolved past escalation" },
  ROLLBACK_NOT_ALLOWED: { label: "Rollback not allowed" },
  CREDIT_FAILED: { label: "Credit failed" },
  STRANDED_PAST_DEADLINE: { label: "Stranded past deadline" },
  LATE_COMMIT_AFTER_RELEASE: { label: "Late commit after release" },
  CONSERVATION: { label: "Conservation breach" },
};

export function recoveryKindLabel(kind: string): string {
  return KINDS[kind]?.label ?? kind;
}

const SEVERITIES: Record<string, { label: string; tone: Tone }> = {
  CRITICAL: { label: "Critical", tone: "danger" },
  HIGH: { label: "High", tone: "warning" },
};

export function recoverySeverityLabel(severity: string): string {
  return SEVERITIES[severity]?.label ?? severity;
}

export function recoverySeverityTone(severity: string): Tone {
  return SEVERITIES[severity]?.tone ?? "muted";
}

const STATES: Record<string, { label: string; tone: Tone }> = {
  OPEN: { label: "Open", tone: "warning" },
  RESOLVED: { label: "Resolved", tone: "success" },
};

export function recoveryStateLabel(state: string): string {
  return STATES[state]?.label ?? state;
}

export function recoveryStateTone(state: string): Tone {
  return STATES[state]?.tone ?? "muted";
}

const QUEUES: Record<string, { label: string }> = {
  FINANCE_OPS: { label: "Finance operations" },
  PROVIDER_INTEGRATION: { label: "Provider integration" },
};

export function recoveryQueueLabel(queue: string): string {
  return QUEUES[queue]?.label ?? queue;
}

const RESULTS: Record<string, { label: string; tone: Tone }> = {
  ACCEPTED: { label: "Accepted", tone: "success" },
  DONE: { label: "Done", tone: "success" },
  REFUSED: { label: "Refused", tone: "danger" },
};

export function recoveryResultLabel(result: string): string {
  return RESULTS[result]?.label ?? result;
}

export function recoveryResultTone(result: string): Tone {
  return RESULTS[result]?.tone ?? "muted";
}

/** What each action does, in the words the confirmation dialog uses. */
export interface RecoveryActionCopy {
  /** The button. */
  label: string;
  /** The dialog's question. */
  title: string;
  /** What the action does — and what it can never do. */
  description: string;
  /** Money-moving (or money-requeueing) actions confirm in the danger tone. */
  tone: "primary" | "danger";
  /** resolve: broker-service requires a note. */
  noteRequired: boolean;
}

export const RECOVERY_ACTION_COPY: Record<RecoveryAction, RecoveryActionCopy> = {
  "status-check": {
    label: "Check status",
    title: "Check the provider status",
    description:
      "Asks the provider for the status of the original transaction id, now. Read-only on the provider: nothing is sent that could move money.",
    tone: "primary",
    noteRequired: false,
  },
  "retry-identical": {
    label: "Retry identically",
    title: "Retry the identical request",
    description:
      "Re-sends the identical request — same transaction id, same fingerprint, same amount. broker-service allows it only after the provider answered TRANSACTION_NOT_FOUND for that id, so it can never create a second money effect.",
    tone: "danger",
    noteRequired: false,
  },
  "finalize-local": {
    label: "Finalize locally",
    title: "Finalize the local side",
    description:
      "Finishes the local side of an operation whose provider outcome is already authoritative — the ledger sync, the order callback, the prepaid release. Moves no provider money.",
    tone: "primary",
    noteRequired: false,
  },
  "deliver-credit": {
    label: "Deliver the Credit",
    title: "Deliver the owed Credit",
    description:
      "Requeues delivery of the owed Credit under its own permanent transaction id. The amount is the one already owed; nothing here can change it.",
    tone: "danger",
    noteRequired: false,
  },
  rollback: {
    label: "Request Rollback",
    title: "Request a full Rollback",
    description:
      "Requests the full Rollback of the committed Deduct. broker-service accepts it only while the funding is Rollback-eligible: never after a fill or any other economic effect, and never while the order's engine state is unresolved.",
    tone: "danger",
    noteRequired: false,
  },
  resolve: {
    label: "Resolve the case",
    title: "Resolve this case",
    description:
      "Closes the case once the evidence shows the recovery is complete. Moves nothing, anywhere. The note is required: say what shows the recovery is complete.",
    tone: "primary",
    noteRequired: true,
  },
};

/** The actions broker-service would accept now (an absent list accepts none). */
export function eligibleActions(c: Pick<RecoveryCaseItem, "eligible_actions"> | undefined): Set<string> {
  return new Set(c?.eligible_actions ?? []);
}

/**
 * The classifier verdict from the case's evidence, when the evidence names
 * one. The evidence is free-form JSON (contracts RecoveryCaseItem.Evidence),
 * so this reads the likely keys and the full JSON is always shown beside it.
 */
export function classifierVerdict(evidence: unknown): string | undefined {
  if (!evidence || typeof evidence !== "object" || Array.isArray(evidence)) return undefined;
  const e = evidence as Record<string, unknown>;
  for (const key of ["classifier_verdict", "classifier", "verdict", "outcome_class"]) {
    const v = e[key];
    if (typeof v === "string" && v.trim()) return v;
  }
  return undefined;
}

/** The evidence as indented JSON ("—" when there is none). */
export function evidenceJSON(evidence: unknown): string {
  if (evidence === undefined || evidence === null) return "—";
  if (typeof evidence === "string") {
    try {
      return JSON.stringify(JSON.parse(evidence), null, 2);
    } catch {
      return evidence;
    }
  }
  return JSON.stringify(evidence, null, 2);
}

// ── the provider attempt log ─────────────────────────────────────────

// contracts brokerv1.ProviderEndpoint: which provider call an attempt was.
// A status read and the money call itself must never look alike.
const ENDPOINTS: Record<string, { label: string; money: boolean }> = {
  "/wallet/v1/Deduct": { label: "Deduct", money: true },
  "/wallet/v1/Credit": { label: "Credit", money: true },
  "/wallet/v1/Rollback": { label: "Rollback", money: true },
  "/wallet/v1/GetTransactionStatus": { label: "Status check", money: false },
  "/wallet/v1/GetBalance": { label: "Balance read", money: false },
  "/integration/v1/ExchangeLaunchCode": { label: "Launch code exchange", money: false },
};

export function attemptEndpointLabel(endpoint: string | undefined): string {
  if (!endpoint) return "—";
  return ENDPOINTS[endpoint]?.label ?? endpoint;
}

/** True for a Deduct / Credit / Rollback attempt — a request that can move money. */
export function attemptMovesMoney(endpoint: string | undefined): boolean {
  return Boolean(endpoint && ENDPOINTS[endpoint]?.money);
}

/** The provider client's classifier verdict (contracts brokerv1.ProviderRetryClass), one map with the launch audit. */
export const attemptOutcomeLabel = launchOutcomeClassLabel;

const OUTCOME_TONES: Record<string, Tone> = {
  committed: "success",
  definitive_failure: "danger",
  not_executed: "muted",
  retryable_read: "warning",
  ambiguous_money: "danger",
  contract_violation: "danger",
};

export function attemptOutcomeTone(outcomeClass: string): Tone {
  return OUTCOME_TONES[outcomeClass] ?? "muted";
}
